import React, { useLayoutEffect } from "react";

import { isEmpty } from "lodash/fp";
import { useSearchParams } from "react-router-dom";

import { Typography } from "@mui/material";

import useSearchMeetings from "@app/analytics/hooks/meetings/useSearchMeetings";
import * as Events from "@app/analytics/tracking/events";
import useSortData from "@app/common/hooks/useSortData";
import { pluralizePhrase } from "@app/common/utils";
import { usePageTitle } from "@app/utils/hooks";

import Stack, { Row } from "@rui/Stack";

import NoResults from "./NoResults";
import SearchResultList from "./SearchResultList";
import SearchResultSortMenu, { SORT_OPTIONS } from "./SearchResultSortMenu";

const SearchResults = () => {
  usePageTitle("Search Results");

  const [queryParams] = useSearchParams();
  const query = queryParams.get("q") ?? "";

  const {
    setSearchQuery,
    searchResults: results,
    isLoading,
  } = useSearchMeetings();

  useLayoutEffect(() => {
    Events.searchMeetings({ term: query });
    setSearchQuery(query);
  }, [query]);

  const {
    selectedSort,
    sortedData: sortedResults,
    onSortChange,
  } = useSortData(results, SORT_OPTIONS);

  if (isLoading && isEmpty(results)) {
    return (
      <Stack variant="center" sx={{ height: "100%" }}>
        <Typography>Searching your meetings...</Typography>
      </Stack>
    );
  }

  if (isEmpty(results)) {
    return <NoResults query={query} />;
  }

  return (
    <Stack sx={{ pb: 4 }}>
      <Row variant="left" spacing={1}>
        <Typography
          variant="bodySecondaryReg"
          sx={{ color: "shades.neutral.600" }}
        >
          Showing {pluralizePhrase(results.length, "result")}
        </Typography>
        <SearchResultSortMenu
          selected={selectedSort}
          onSortChange={onSortChange}
        />
      </Row>
      <SearchResultList query={query} results={sortedResults} />
    </Stack>
  );
};

export default SearchResults;
